import { normalizeBoard } from './BoardPack.mjs';
import { compareBoards } from './BoardDiff.mjs';
import { saveRevision, adoptRevision, sameBoard, isOriginal } from './Library.mjs';
import { Game } from './Game';
import { TestUI } from './TestUI';

const $ = id => document.getElementById(id);
const label = revision => revision.id === 'draft' ? 'Current draft' : `Revision ${revision.number}`;
const symbol = char => char === ' ' ? 'space' : char;

export const RevisionUI = {
    isOpen: false,
    init(workshop) {
        this.workshop = workshop;
        $('preview-compare').onclick = () => this.open(workshop.browser.previewId);
        $('workshop-compare').onclick = () => this.open(workshop.current().id);
        $('save-revision').onclick = () => workshop.attempt(() => {
            normalizeBoard(workshop.current()); workshop.remember();
            const revision = saveRevision(workshop.library, workshop.current().id);
            workshop.changed(); workshop.notify(`Revision ${revision.number} saved. Existing Series keep their selected revisions.`);
        });
        $('compare-board').onchange = () => this.open($('compare-board').value);
        $('compare-context').onchange = () => {
            this.context = $('compare-context').value;
            const entry = this.target();
            if (entry) this.leftId = entry.set.revisionIds[entry.index];
            this.render();
        };
        $('compare-before').onchange = () => { this.leftId = $('compare-before').value; this.render(); };
        $('compare-after').onchange = () => { this.rightId = $('compare-after').value; this.render(); };
        $('diff-only').onchange = () => this.render();
        $('large-maps').onchange = () => document.querySelector('.comparison-maps').classList.toggle('large', $('large-maps').checked);
        for (const side of ['before', 'after']) $('test-' + side).onclick = () => workshop.attempt(() => {
            const revision = this.revision(side === 'before' ? this.leftId : this.rightId);
            TestUI.start(revision, { returnView: 'workshop', returnLabel: 'Return to comparison', returnFocus: 'test-' + side, context: label(revision) });
        });
        $('compare-save').onclick = () => workshop.attempt(() => {
            normalizeBoard(this.board()); workshop.remember();
            this.rightId = saveRevision(workshop.library, this.boardId).id;
            workshop.changed(); this.render();
            workshop.notify('Revision saved. Choose Use this revision to update the selected Series entry.');
        });
        $('adopt-revision').onclick = () => workshop.attempt(() => {
            const entry = this.target();
            if (!entry || this.rightId === 'draft') throw new Error('Select a Series entry and a saved revision.');
            workshop.remember(); adoptRevision(workshop.library, entry.set.id, entry.index, this.rightId);
            this.leftId = this.rightId;
            workshop.changed(); this.render(); workshop.notify('This Series entry now uses the selected revision. Other entries are unchanged.');
        });
        $('keep-revision').onclick = () => this.close();
    },
    close(focus = true) {
        this.isOpen = false;
        $('compare-view').hidden = true;
        $('workshop-compare').setAttribute('aria-expanded', 'false');
        if (focus) $('workshop-compare').focus();
    },
    board() { return this.workshop.boards.find(board => board.id === this.boardId); },
    revision(id) { return id === 'draft' ? { ...this.board(), id: 'draft' } : this.board()?.revisions?.find(revision => revision.id === id); },
    target() {
        const [setId, index] = (this.context || '').split('/');
        const set = this.workshop.library.sets.find(item => item.id === setId);
        return set && set.boardIds[Number(index)] === this.boardId ? { set, index: Number(index) } : null;
    },
    open(boardId, setId, index) {
        if (isOriginal(boardId) || setId === 'original') {
            this.workshop.notify('Original boards are permanently locked and have no revision history. Make a custom copy to create a variant.');
            return;
        }
        // Returning from a test should retain the exact pair being reviewed.
        if (this.boardId !== boardId || setId) {
            this.boardId = boardId; this.context = setId ? `${setId}/${index}` : '';
            const revisions = this.board()?.revisions || [];
            this.leftId = this.target()?.set.revisionIds[index] || revisions.at(-2)?.id || revisions.at(-1)?.id || 'draft';
            const latest = revisions.at(-1);
            this.rightId = latest && sameBoard(latest, this.board()) ? latest.id : 'draft';
            if (setId && latest && latest.id !== this.leftId) this.rightId = latest.id;
        }
        // Comparison belongs to the editor; keep its board selected when returning.
        const selected = this.workshop.boards.findIndex(board => board.id === this.boardId);
        if (selected >= 0 && selected !== this.workshop.selected) {
            this.workshop.selected = selected;
            this.workshop.changed();
        }
        this.isOpen = true;
        $('workshop-compare').setAttribute('aria-expanded', 'true');
        this.workshop.show('workshop');
        $('compare-view').scrollIntoView({ block: 'start' });
    },
    render() {
        if (!this.board()) {
            this.boardId = this.workshop.current().id; this.leftId = this.rightId = 'draft'; this.context = '';
        }
        const board = this.board(), history = board.revisions || [];
        if (!this.revision(this.leftId)) this.leftId = history.at(-1)?.id || 'draft';
        if (!this.revision(this.rightId)) this.rightId = 'draft';
        $('compare-board').replaceChildren(...this.workshop.boards.map(item => new Option(item.name || 'Untitled', item.id)));
        $('compare-board').value = board.id;
        const options = [new Option('Review only — no Series selected', '')];
        for (const set of this.workshop.library.sets) set.boardIds.forEach((id, i) => { if (id === board.id) options.push(new Option(`${set.name} · Level ${i + 1}`, `${set.id}/${i}`)); });
        if (!options.some(option => option.value === this.context)) this.context = '';
        $('compare-context').replaceChildren(...options); $('compare-context').value = this.context;
        for (const [id, value] of [['compare-before', this.leftId], ['compare-after', this.rightId]]) {
            $(id).replaceChildren(...history.map(revision => new Option(label(revision), revision.id)), new Option('Current draft', 'draft')); $(id).value = value;
        }
        const before = this.revision(this.leftId), after = this.revision(this.rightId);
        const diff = compareBoards(before, after);
        $('compare-heading').textContent = board.name || 'Untitled board';
        $('diff-count').textContent = `${diff.changes.length} changed cell${diff.changes.length === 1 ? '' : 's'}`;
        for (const type of ['added', 'removed', 'replaced']) $('diff-' + type).textContent = `${diff.totals[type]} ${type}`;
        const summary = [];
        if (diff.nameChanged) summary.push(`Name changed: “${before.name}” → “${after.name}”.`);
        if (diff.playerMoved) summary.push(`Player start moved from row ${diff.oldPlayer[0].y + 1}, column ${diff.oldPlayer[0].x + 1} to row ${diff.newPlayer[0].y + 1}, column ${diff.newPlayer[0].x + 1}.`);
        summary.push(...diff.tileChanges.map(item => `${item.label}: ${item.before} → ${item.after}.`));
        $('diff-summary').textContent = summary.join(' ') || (diff.changes.length ? 'Tile positions or decorative characters changed.' : 'These maps and names are identical.');
        const changes = new Map(diff.changes.map(change => [`${change.x},${change.y}`, change]));
        for (const [side, rows, revision] of [['before', diff.left, before], ['after', diff.right, after]]) {
            $('name-' + side).textContent = revision.name || 'Untitled';
            const grid = $('map-' + side); grid.replaceChildren();
            rows.forEach((row, y) => {
                for (let x = 0; x < row.length; x++) {
                    const cell = document.createElement('span'), change = changes.get(`${x},${y}`);
                    cell.textContent = change && row[x] === ' ' ? '·' : row[x];
                    cell.className = change ? `diff-cell ${change.type}` : $('diff-only').checked ? 'unchanged dim' : 'unchanged';
                    if (change) cell.title = `Row ${y + 1}, column ${x + 1}: ${symbol(change.before)} → ${symbol(change.after)} (${change.type})`;
                    grid.append(cell);
                }
                grid.append(document.createTextNode('\n'));
            });
            try { normalizeBoard(revision); $('status-' + side).textContent = 'Format valid · test to check the route'; $('test-' + side).disabled = !Game.ready; }
            catch (error) { $('status-' + side).textContent = error.message; $('test-' + side).disabled = true; }
        }
        $('diff-details').replaceChildren();
        for (const change of diff.changes) {
            const row = document.createElement('tr');
            for (const text of [change.y + 1, change.x + 1, symbol(change.before), symbol(change.after), change.type]) { const cell = document.createElement('td'); cell.textContent = text; row.append(cell); }
            $('diff-details').append(row);
        }
        const entry = this.target(), current = entry && this.revision(entry.set.revisionIds[entry.index]);
        $('adoption-context').textContent = entry ? `${entry.set.name}, level ${entry.index + 1}, currently uses ${label(current)}. Only this entry will be updated.` : 'Choose a Series entry above to adopt a saved revision. Reviewing or testing does not change a Series.';
        let valid = true; try { normalizeBoard(after); } catch { valid = false; }
        $('adopt-revision').disabled = !entry || !valid || this.rightId === 'draft' || current?.id === this.rightId;
        $('compare-save').hidden = this.rightId !== 'draft'; $('compare-save').disabled = !valid;
        $('revision-note').textContent = history.length ? `${history.length} saved revision${history.length === 1 ? '' : 's'}. Draft edits do not change saved revisions or Series.` : 'No saved revisions yet. Save your draft to create the first revision.';
    }
};
